

import { useEffect, useRef } from "react"

const RichTextEditor = ({ value, onChange, placeholder, type = "rich_text" }) => {
  const editorRef = useRef(null)
  const quillRef = useRef(null)

  useEffect(() => {
    // Initialize Quill editor for rich text
    if (type === "rich_text" && !quillRef.current) {
      // Load Quill dynamically
      const script = document.createElement("script")
      script.src = "https://cdn.quilljs.com/1.3.6/quill.min.js"
      script.onload = () => {
        const link = document.createElement("link")
        link.href = "https://cdn.quilljs.com/1.3.6/quill.snow.css"
        link.rel = "stylesheet"
        document.head.appendChild(link)

        if (window.Quill && editorRef.current) {
          quillRef.current = new window.Quill(editorRef.current, {
            theme: "snow",
            placeholder: placeholder || "Enter content...",
            modules: {
              toolbar: [
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ color: [] }, { background: [] }],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ indent: "-1" }, { indent: "+1" }],
                [{ align: [] }],
                ["link", "image", "video"],
                ["blockquote", "code-block"],
                ["clean"],
              ],
            },
          })

          // Set initial content
          if (value) {
            quillRef.current.root.innerHTML = value
          }

          // Listen for changes
          quillRef.current.on("text-change", () => {
            const content = quillRef.current.root.innerHTML
            onChange(content)
          })
        }
      }
      document.head.appendChild(script)
    }

    return () => {
      if (quillRef.current) {
        quillRef.current = null
      }
    }
  }, [type])

  // Update content when value changes externally
  useEffect(() => {
    if (quillRef.current && value !== quillRef.current.root.innerHTML) {
      quillRef.current.root.innerHTML = value || ""
    }
  }, [value])

  if (type === "markdown") {
    return (
      <div className="markdown-editor">
        <div className="row">
          <div className="col-md-6">
            <label className="form-label">Markdown Input</label>
            <textarea
              className="form-control"
              rows="12"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder || "Enter markdown content..."}
              style={{ fontFamily: "monospace", maxHeight: "350px", overflowY: "auto" }}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Preview</label>
            <div
              className="border p-3"
              style={{ height: "350px", backgroundColor: "#f8f9fa", overflowY: "auto" }}
              dangerouslySetInnerHTML={{
                __html: value ? markdownToHtml(value) : '<p class="text-muted">Preview will appear here...</p>',
              }}
            />
          </div>
        </div>
      </div>
    )
  }

  if (type === "html") {
    return (
      <div className="html-editor">
        <div className="row">
          <div className="col-md-6">
            <label className="form-label">HTML Input</label>
            <textarea
              className="form-control"
              rows="12"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder || "Enter HTML content..."}
              style={{ fontFamily: "monospace", maxHeight: "350px", overflowY: "auto" }}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Preview</label>
            <div
              className="border p-3"
              style={{ height: "350px", backgroundColor: "#f8f9fa", overflowY: "auto" }}
              dangerouslySetInnerHTML={{
                __html: value || '<p class="text-muted">Preview will appear here...</p>',
              }}
            />
          </div>
        </div>
      </div>
    )
  }

  if (type === "static") {
    return (
      <textarea
        className="form-control"
        rows="8"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "Enter plain text content..."}
        style={{ maxHeight: "300px", overflowY: "auto" }}
      />
    )
  }

  // Rich text editor (default)
  return (
    <div className="rich-text-editor">
      <div ref={editorRef} style={{ minHeight: "300px", maxHeight: "400px", overflowY: "auto" }} />
    </div>
  )
}

// Simple markdown to HTML converter (basic implementation)
const markdownToHtml = (markdown) => {
  return markdown
    .replace(/^### (.*$)/gim, "<h3>$1</h3>")
    .replace(/^## (.*$)/gim, "<h2>$1</h2>")
    .replace(/^# (.*$)/gim, "<h1>$1</h1>")
    .replace(/^> (.*$)/gim, "<blockquote>$1</blockquote>")
    .replace(/\*\*(.*)\*\*/gim, "<strong>$1</strong>")
    .replace(/\*(.*)\*/gim, "<em>$1</em>")
    .replace(/!\[([^\]]*)\]$$([^$$]*)\)/gim, '<img alt="$1" src="$2" />')
    .replace(/\[([^\]]*)\]$$([^$$]*)\)/gim, '<a href="$2">$1</a>')
    .replace(/\n$/gim, "<br />")
}

export default RichTextEditor